import { useState, useCallback, useRef } from 'react';

export interface ConfirmOptions {
  title?: string;
  message?: string;
  itemName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
}

/**
 * Promise-based confirmation hook. Returns props to spread onto a ConfirmDialog
 * and a `confirm()` function that resolves to `true` (confirmed) or `false` (cancelled).
 *
 * Usage:
 * ```tsx
 * const { confirm, confirmProps } = useConfirm();
 *
 * const handleDelete = async () => {
 *   const ok = await confirm({
 *     title: 'Delete Resident',
 *     message: 'This cannot be undone.',
 *     itemName: resident.fullName,
 *     confirmLabel: 'Delete',
 *     variant: 'danger',
 *   });
 *   if (ok) deleteMutation.mutate(resident.id);
 * };
 *
 * return <ConfirmDialog {...confirmProps} />;
 * ```
 */
export function useConfirm() {
  const [state, setState] = useState<ConfirmState>({ open: false });
  const resolverRef = useRef<((val: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions = {}): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ ...options, open: true });
    });
  }, []);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
    resolverRef.current?.(true);
    resolverRef.current = null;
  }, []);

  return {
    confirm,
    confirmProps: {
      open: state.open,
      onClose: handleClose,
      onConfirm: handleConfirm,
      title: state.title,
      message: state.message,
      itemName: state.itemName,
      confirmLabel: state.confirmLabel,
      cancelLabel: state.cancelLabel,
      variant: state.variant,
    },
  };
}
